import {useAtom} from "jotai";
import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";
import {toast, ToastContainer} from "react-toastify";
import styled from "styled-components";
import {multiAxiosCall} from "../../common/StringUtils";
import {selPolicyTermsById, updateTerms} from "../../services/platform/ManageUserAxios";
import {tokenResultAtom} from "../login/entity";
import {modalController} from "../../store";
import PlatformSectionHeader from "./PlatformSectionHeader";

export default function PlatformTermDetail() {
  const [termsInfo,setTermsInfo]=useState();
  const [tokenResultInfo]=useAtom(tokenResultAtom);
  const [,setModal]=useAtom(modalController);
  const {handleSubmit}=useForm({mode:"onSubmit",defaultValues:termsInfo});
  const navigate=useNavigate();
  const {state}=useLocation();

  useEffect(()=>{
    multiAxiosCall([selPolicyTermsById(state.id)],response=>setTermsInfo(response[0][0]));
    return ()=>setModal({isShow:false});
  },[setModal,state.id]);

  const onSubmit=()=>{
    multiAxiosCall([updateTerms(termsInfo)],response=>{
      if(response[0]){
        toast.success("약관 정보가 수정되었습니다.",{onClose:()=>tokenResultInfo.role==="NORMAL"?navigate("/board/dashboard"):navigate("/board/platformTerm"),autoClose:100});
      }else toast.warning("약관 정보 수정에 실패했습니다. 관리자에게 문의해 주세요.");
    });
  };

  return <>
    <TermDetailForm onSubmit={handleSubmit(onSubmit,console.log)}>
      <TermDetailCard>
        <PlatformSectionHeader title="약관 상세" description="약관명과 내용을 확인하고 필요한 정보를 수정합니다."/>
        <TermDetailBody>
          <FieldGroup>
            <FieldLabel>약관명 <RequiredMark>*</RequiredMark></FieldLabel>
            <FieldDescription>사용자에게 표시되는 약관의 제목입니다.</FieldDescription>
            <TermTitleInput type="text" placeholder="약관명을 입력해 주세요." value={termsInfo?.title||""}
              onChange={event=>setTermsInfo(prev=>({...prev,title:event.target.value}))}/>
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>약관 내용 <RequiredMark>*</RequiredMark></FieldLabel>
            <FieldDescription>사용자에게 안내할 약관의 전체 내용을 입력해 주세요.</FieldDescription>
            <TermContentEditor key={termsInfo?.id??"term-content"} contentEditable suppressContentEditableWarning
              dangerouslySetInnerHTML={{__html:termsInfo?.content||""}}
              onInput={event=>setTermsInfo(prev=>({...prev,content:event.currentTarget.innerHTML}))}
              data-placeholder="약관 내용을 입력해 주세요."/>
            <ContentGuide>입력한 서식과 문장은 HTML 형식으로 저장됩니다.</ContentGuide>
          </FieldGroup>
        </TermDetailBody>
      </TermDetailCard>
      <TermActions>
        <div><ActionTitle>약관 정보 설정</ActionTitle><ActionDescription>수정 내용을 확인한 후 저장하거나 목록으로 돌아갈 수 있습니다.</ActionDescription></div>
        <ActionButtons><Cancel type="button" onClick={()=>navigate("/board/platformTerm")}>취소</Cancel><Submit type="submit">정보 수정</Submit></ActionButtons>
      </TermActions>
    </TermDetailForm>
    <ToastContainer position="top-center" autoClose={1500} hideProgressBar limit={1} style={{zIndex:9999999}}/>
  </>;
}

const TermDetailForm=styled.form`width:100%;`;
const TermDetailCard=styled.section`overflow:hidden;border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface);box-shadow:0 5px 18px rgba(31,41,55,.045);`;
const TermDetailBody=styled.div`display:flex;flex-direction:column;gap:20px;padding:24px;`;
const FieldGroup=styled.section`padding:18px;border:1px solid #e5eaf0;border-radius:9px;background:#fafbfc;`;
const FieldLabel=styled.label`display:block;margin-bottom:5px;color:#333;font-size: var(--font-size-14);font-weight:700;`;
const RequiredMark=styled.span`color:var(--color-primary);`;
const FieldDescription=styled.p`margin:0 0 12px;color:#8a9299;font-size: var(--font-size-11);line-height:1.5;`;
const TermTitleInput=styled.input`
  width:100%;height:42px;box-sizing:border-box;padding:0 12px;border:1px solid #dfe3e7;border-radius:8px;background:var(--color-surface);color:#333;font-size: var(--font-size-13);
  &:hover{border-color:var(--color-primary-border);}&:focus{border-color:var(--color-primary);outline:0;box-shadow:0 0 0 3px rgba(59,130,246,.1);}
`;
const TermContentEditor=styled.div`
  width:100%;min-height:580px;max-height:760px;box-sizing:border-box;overflow-y:auto;padding:18px;border:1px solid #dfe3e7;border-radius:8px;background:var(--color-surface);
  color:#444;font-family:inherit;font-size: var(--font-size-13);line-height:1.75;
  &:hover{border-color:var(--color-primary-border);}&:focus{border-color:var(--color-primary);outline:0;box-shadow:0 0 0 3px rgba(59,130,246,.1);}
  &:empty::before{color:#aaa;content:attr(data-placeholder);pointer-events:none;}
  p{margin:0 0 10px;}ul,ol{margin:8px 0;padding-left:22px;}table{width:100%;border-collapse:collapse;}th,td{padding:8px;border:1px solid var(--color-border);}
`;
const ContentGuide=styled.p`margin:8px 0 0;color:#999;font-size: var(--font-size-11);`;
const TermActions=styled.div`
  position:sticky;bottom:0;z-index:5;display:flex;box-sizing:border-box;align-items:center;justify-content:space-between;gap:12px;margin-top:20px;padding:16px 20px;
  border:1px solid var(--color-primary-border);border-left:4px solid var(--color-primary);border-radius:10px;background:linear-gradient(90deg,#f5f9ff 0%,rgba(255,255,255,.98) 48%);
  box-shadow:0 -4px 20px rgba(59,130,246,.08);@media(max-width:640px){align-items:stretch;flex-direction:column;}
`;
const ActionTitle=styled.strong`display:block;color:#2f3438;font-size: var(--font-size-14);font-weight:800;`;
const ActionDescription=styled.span`display:block;margin-top:4px;color:#7f878e;font-size: var(--font-size-12);`;
const ActionButtons=styled.div`display:flex;gap:8px;@media(max-width:640px){button{flex:1;}}`;
const Cancel=styled.button`min-width:86px;height:40px;border:1px solid #dfe3e7;border-radius:7px;background:var(--color-surface);color:#666;font-size: var(--font-size-13);font-weight:650;cursor:pointer;`;
const Submit=styled(Cancel)`border-color:var(--color-primary-strong);background:var(--color-primary-strong);color:var(--color-surface);&:hover{background:var(--color-primary-dark);}`;
